// @ts-check
import { useSelector as useReduxSelector } from 'react-redux';
import SceneHelper from 'utils/SceneHelper';
import { modes } from 'constants/index';

/** @typedef {import('actions/scene.types').StoreState} StoreState */
/** @type {import('react-redux').TypedUseSelectorHook<StoreState>} */
const useStoreSelector = useReduxSelector;

/**
 * Returns whether the order button should be visible for the current editor state.
 * Hides the button for admins and when the user is sorting pages,
 * but keeps it visible in single-page experiences that never switch to all-pages review.
 *
 * @returns {boolean}
 */
export const useIsShowOrderButton = () => {
  return useStoreSelector((storeState) => {
    const mode = SceneHelper.getMode(storeState);
    const admin = SceneHelper.getAdmin(storeState);
    const isSortPages = SceneHelper.getIsSortPages(storeState);
    const viewAllPages = SceneHelper.getViewAllPages(storeState);

    const isEligibleMode = mode === modes.canvas || mode === modes.mask || mode === modes.poster;
    const isAllPagesOverview = viewAllPages && !isSortPages;
    const isAdminBlocked = !!admin;

    return !isAdminBlocked && (isAllPagesOverview || isEligibleMode);
  });
};

/**
 * Returns whether ordering should be disabled given photo upload progress and page constraints.
 * Locks the button until an image is fully uploaded and, for products with sheet-pair requirements,
 * waits for every sheet pair to be complete.
 *
 * @returns {boolean}
 */
export const useIsDisabled = () => {
  return useStoreSelector((storeState) => {
    const mode = SceneHelper.getMode(storeState);
    const isOneImageLoading = SceneHelper.getIsOneImageLoading(storeState);

    const baseDisabled = isOneImageLoading;
    if (mode !== modes.photoBook) return baseDisabled;

    const numberOfPages = SceneHelper.getCountPages(storeState);
    const countPagesInOneLetter = SceneHelper.getCountPagesInOneLetter(storeState);
    const remainingPagesForSheet = !!((numberOfPages - 1) % countPagesInOneLetter);

    // Ensure the product-specific sheet requirement is met (e.g., paired sides in photobooks).
    return !!(baseDisabled || remainingPagesForSheet);
  });
};
